import { noop } from '@tanstack/react-query';
import { createFileRoute, Outlet, redirect } from '@tanstack/react-router';
import { useEffect, useRef } from 'react';
import { auth0Exports, authUser, redirectToSessionEnded } from '@/features/auth/utils';
import { useIdleLogout } from '@/features/auth/helpers';
import { isReauthenticationError } from '@/features/auth/utils';
import { LoadingState } from '@/ui/components/loading-state';
import { onboardingQuery } from '@/features/user/queries';
import { uploadCapabilitiesQuery } from '@/features/documents/queries';
import type { FileRouteTypes } from '@/routeTree.gen';
import { tryCatch } from '@/try-catch';
import { QueryStreams } from '@/features/workspaces/components/query-streams';
import { IntercomMessenger } from '@/features/intercom/components/intercom-messenger';

export const Route = createFileRoute('/_authed')({
  component: RouteComponent,
  beforeLoad: async (opts) => {
    await auth0Exports.waitUntilReady();
    const target = opts.location.pathname + opts.location.searchStr;

    if (!auth0Exports.getIsAuthenticated()) throw redirect({ to: '/login', search: { target } });

    const onboarding = await opts.context.queryClient.query({
      ...onboardingQuery(),
      staleTime: 'static',
    });
    if (!onboarding) throw redirect({ to: '/login', search: { target } });

    const context = { auth: { ...opts.context.auth, user: authUser(onboarding) } };
    if (onboarding.hasActiveOrganization) return context;

    const exempt: FileRouteTypes['id'][] = [
      '/_authed/accept-invitation',
      '/_authed/add-organization',
      '/_authed/choose-organization',
      '/_authed/go/teams-link/$state',
    ];
    if (opts.matches.some((m) => exempt.includes(m.routeId))) return context;

    throw redirect({ to: '/choose-organization' });
  },
  loader: ({ context }) => {
    context.queryClient.query(uploadCapabilitiesQuery()).catch(noop);
  },
  pendingComponent: LoadingState,
});

function RouteComponent() {
  return (
    <>
      <QueryStreams />
      <IntercomMessenger />
      <OutletWithSessionExpiredCheck />
    </>
  );
}

function OutletWithSessionExpiredCheck() {
  useIdleLogout();
  const isLoggingOut = useRef(false);

  useEffect(() => {
    const checkSession = async () => {
      if (isLoggingOut.current) return;

      const getToken = auth0Exports.getAccessTokenSilently();
      if (!getToken) return;
      const { error } = await tryCatch(getToken());
      if (isReauthenticationError(error)) {
        isLoggingOut.current = true;
        redirectToSessionEnded('sessionExpired');
      }
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') checkSession();
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('focus', checkSession);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('focus', checkSession);
    };
  }, []);

  return <Outlet />;
}
